<template>
  <div class="admin-banners">
    <div class="page-header">
      <h1>배너 관리</h1>
      <div class="header-actions">
        <button class="btn-secondary" @click="showJobModal = true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          공고에서 추가
        </button>
        <button class="btn-primary" @click="openAddModal">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          직접 추가
        </button>
      </div>
    </div>

    <!-- 배너 목록 -->
    <div class="banner-list">
      <div v-if="banners.length === 0" class="empty-state">
        <p>등록된 배너가 없습니다</p>
      </div>
      
      <div v-for="(banner, index) in banners" :key="banner.id" class="banner-item">
        <div class="banner-order">
          <button 
            class="order-btn" 
            :disabled="index === 0"
            @click="moveUp(index)"
          >▲</button>
          <span>{{ index + 1 }}</span>
          <button 
            class="order-btn" 
            :disabled="index === banners.length - 1"
            @click="moveDown(index)"
          >▼</button>
        </div>
        
        <div class="banner-preview">
          <img :src="banner.image" :alt="banner.title" />
        </div>
        
        <div class="banner-info">
          <div class="banner-title-row">
            <h3>{{ banner.title }}</h3>
            <div class="banner-badges">
              <span v-if="banner.isPremium" class="badge premium">프리미엄</span>
              <span v-if="banner.isPick" class="badge pick">PICK</span>
              <span v-if="banner.isUrgent" class="badge urgent">급구</span>
              <span v-if="banner.isNew" class="badge new">신규</span>
              <span v-if="banner.jobId" class="badge job">공고 연결</span>
            </div>
          </div>
          <p class="banner-meta">
            <span v-if="banner.region">📍 {{ banner.region }}</span>
            <span v-if="banner.industry">{{ banner.industry }}</span>
            <span v-if="banner.salary">{{ banner.salary }}</span>
          </p>
          <p class="banner-link">{{ banner.link }}</p>
        </div>
        
        <div class="banner-actions">
          <label class="toggle">
            <input 
              type="checkbox" 
              :checked="banner.isActive" 
              @change="toggleActive(banner)"
            />
            <span class="toggle-slider"></span>
          </label>
          <button class="btn-icon" @click="openEditModal(banner)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button class="btn-icon delete" @click="deleteBanner(banner.id)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- 배너 추가/수정 모달 -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ isEditing ? '배너 수정' : '배너 추가' }}</h2>
          <button class="close-btn" @click="closeModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label>제목 *</label>
            <input v-model="form.title" type="text" placeholder="배너 제목" />
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>지역</label>
              <select v-model="form.region">
                <option value="">선택</option>
                <option v-for="r in regions" :key="r" :value="r">{{ r }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>업종</label>
              <select v-model="form.industry">
                <option value="">선택</option>
                <option v-for="i in industries" :key="i" :value="i">{{ i }}</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>급여</label>
              <input v-model="form.salary" type="text" placeholder="예: 월 300만원~" />
            </div>
            <div class="form-group">
              <label>직종</label>
              <select v-model="form.jobType">
                <option value="">선택</option>
                <option v-for="j in jobTypes" :key="j" :value="j">{{ j }}</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label>링크 *</label>
            <input v-model="form.link" type="text" placeholder="/jobs/xxx 또는 https://..." />
          </div>

          <div class="form-group">
            <label>설명</label>
            <textarea v-model="form.description" placeholder="간단한 설명 (선택)" rows="2"></textarea>
          </div>
          
          <div class="form-group">
            <label>이미지 *</label>
            <div class="image-upload">
              <input type="file" accept="image/*" @change="handleImageUpload" />
              <div v-if="form.image" class="image-preview">
                <img :src="form.image" alt="미리보기" />
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>뱃지</label>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.isPremium" />
                <span class="badge-preview premium">프리미엄</span>
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.isPick" />
                <span class="badge-preview pick">PICK</span>
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.isUrgent" />
                <span class="badge-preview urgent">급구</span>
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.isNew" />
                <span class="badge-preview new">신규</span>
              </label>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label active-check">
              <input type="checkbox" v-model="form.isActive" />
              <span>활성화</span>
            </label>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeModal">취소</button>
          <button class="btn-primary" @click="saveBanner" :disabled="saving">
            {{ saving ? '저장 중...' : (isEditing ? '수정' : '추가') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 공고 선택 모달 -->
    <div v-if="showJobModal" class="modal-overlay" @click.self="showJobModal = false">
      <div class="modal modal-large">
        <div class="modal-header">
          <h2>공고에서 배너 추가</h2>
          <button class="close-btn" @click="showJobModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="search-box">
            <input 
              v-model="jobSearchKeyword" 
              type="text" 
              placeholder="공고 제목, 현장명으로 검색"
            />
          </div>
          
          <div class="job-list">
            <div 
              v-for="job in filteredJobs" 
              :key="job.id" 
              class="job-item"
              @click="selectJob(job)"
            >
              <div class="job-thumb">
                <img v-if="job.thumbnail" :src="job.thumbnail" alt="" />
                <div v-else class="no-image">No Image</div>
              </div>
              <div class="job-info">
                <h4>{{ job.title }}</h4>
                <p>
                  <span v-if="job.region">{{ job.region }}</span>
                  <span v-if="job.industry">{{ job.industry }}</span>
                </p>
                <p class="job-salary" v-if="job.salary">{{ job.salaryType }} {{ job.salary }}</p>
              </div>
              <span v-if="job.isPremium" class="premium-badge">프리미엄</span>
            </div>
            
            <div v-if="filteredJobs.length === 0" class="empty-jobs">
              검색 결과가 없습니다
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/services/firebase'

export default {
  name: 'AdminBannersView',
  setup() {
    const banners = ref([])
    const jobs = ref([])
    const showModal = ref(false)
    const showJobModal = ref(false)
    const isEditing = ref(false)
    const editingId = ref(null)
    const saving = ref(false)
    const jobSearchKeyword = ref('')

    const regions = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주']
    const industries = ['아파트', '오피스텔', '도시형생활주택', '호텔', '레지던스', '상가', '쇼핑몰', '오피스', '지식산업센터', '토지', '빌라', '타운하우스', '펜션', '전원주택', '콘도', '리조트', '기타']
    const jobTypes = ['시행/업무대행', '건설사', '분양대행', '본부장', '본부', '팀장', '팀원', '정규직', '데스크', 'TM상담사', '홍보관도우미', '거점알바', '호객알바', '전단알바', '직투알바', '현수막', '기타']

    const defaultForm = {
      title: '',
      link: '',
      image: '',
      region: '',
      industry: '',
      salary: '',
      jobType: '',
      description: '',
      isPremium: false,
      isPick: false,
      isUrgent: false,
      isNew: false,
      isActive: true,
      jobId: null
    }

    const form = ref({ ...defaultForm })

    const filteredJobs = computed(() => {
      if (!jobSearchKeyword.value) return jobs.value
      const keyword = jobSearchKeyword.value.toLowerCase()
      return jobs.value.filter(job => 
        job.title?.toLowerCase().includes(keyword) ||
        job.siteName?.toLowerCase().includes(keyword)
      )
    })

    const fetchBanners = async () => {
      try {
        const q = query(collection(db, 'banners'), orderBy('order', 'asc'))
        const snapshot = await getDocs(q)
        banners.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('배너 로드 실패:', e)
      }
    }

    const fetchJobs = async () => {
      try {
        const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        jobs.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('공고 로드 실패:', e)
      }
    }

    const openAddModal = () => {
      isEditing.value = false
      editingId.value = null
      form.value = { ...defaultForm }
      showModal.value = true
    }

    const openEditModal = (banner) => {
      isEditing.value = true
      editingId.value = banner.id
      form.value = { 
        title: banner.title || '',
        link: banner.link || '',
        image: banner.image || '',
        region: banner.region || '',
        industry: banner.industry || '',
        salary: banner.salary || '',
        jobType: banner.jobType || '',
        description: banner.description || '',
        isPremium: banner.isPremium || false,
        isPick: banner.isPick || false,
        isUrgent: banner.isUrgent || false,
        isNew: banner.isNew || false,
        isActive: banner.isActive ?? true,
        jobId: banner.jobId || null
      }
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      form.value = { ...defaultForm }
    }

    const handleImageUpload = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      try {
        const fileName = `banners/${Date.now()}_${file.name}`
        const fileRef = storageRef(storage, fileName)
        await uploadBytes(fileRef, file)
        form.value.image = await getDownloadURL(fileRef)
      } catch (e) {
        console.error('이미지 업로드 실패:', e)
        alert('이미지 업로드에 실패했습니다')
      }
    }

    const saveBanner = async () => {
      if (!form.value.title || !form.value.link || !form.value.image) {
        alert('제목, 링크, 이미지는 필수입니다')
        return
      }

      saving.value = true
      try {
        const bannerData = {
          title: form.value.title,
          link: form.value.link,
          image: form.value.image,
          region: form.value.region,
          industry: form.value.industry,
          salary: form.value.salary,
          jobType: form.value.jobType,
          description: form.value.description,
          isPremium: form.value.isPremium,
          isPick: form.value.isPick,
          isUrgent: form.value.isUrgent,
          isNew: form.value.isNew,
          isActive: form.value.isActive,
          jobId: form.value.jobId,
          updatedAt: serverTimestamp()
        }

        if (isEditing.value) {
          await updateDoc(doc(db, 'banners', editingId.value), bannerData)
        } else {
          bannerData.order = banners.value.length
          bannerData.createdAt = serverTimestamp()
          await addDoc(collection(db, 'banners'), bannerData)
        }

        await fetchBanners()
        closeModal()
      } catch (e) {
        console.error('배너 저장 실패:', e)
        alert('저장에 실패했습니다')
      } finally {
        saving.value = false
      }
    }

    const toggleActive = async (banner) => {
      try {
        await updateDoc(doc(db, 'banners', banner.id), {
          isActive: !banner.isActive
        })
        await fetchBanners()
      } catch (e) {
        console.error('상태 변경 실패:', e)
      }
    }

    const deleteBanner = async (id) => {
      if (!confirm('정말 삭제하시겠습니까?')) return
      try {
        await deleteDoc(doc(db, 'banners', id))
        await fetchBanners()
      } catch (e) {
        console.error('삭제 실패:', e)
      }
    }

    const moveUp = async (index) => {
      if (index === 0) return
      const newBanners = [...banners.value]
      ;[newBanners[index - 1], newBanners[index]] = [newBanners[index], newBanners[index - 1]]
      await updateOrders(newBanners)
    }

    const moveDown = async (index) => {
      if (index === banners.value.length - 1) return
      const newBanners = [...banners.value]
      ;[newBanners[index], newBanners[index + 1]] = [newBanners[index + 1], newBanners[index]]
      await updateOrders(newBanners)
    }

    const updateOrders = async (newBanners) => {
      try {
        for (let i = 0; i < newBanners.length; i++) {
          await updateDoc(doc(db, 'banners', newBanners[i].id), { order: i })
        }
        await fetchBanners()
      } catch (e) {
        console.error('순서 변경 실패:', e)
      }
    }

    const selectJob = async (job) => {
      try {
        const bannerData = {
          title: job.title,
          link: `/jobs/${job.id}`,
          image: job.thumbnail || '',
          region: job.region || '',
          industry: job.industry || '',
          salary: job.salary ? `${job.salaryType || ''} ${job.salary}` : '',
          jobType: job.jobType || '',
          description: job.siteName || '',
          isPremium: job.isPremium || false,
          isPick: job.isPick || false,
          isUrgent: false,
          isNew: true,
          isActive: true,
          jobId: job.id,
          order: banners.value.length,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }

        await addDoc(collection(db, 'banners'), bannerData)
        await fetchBanners()
        showJobModal.value = false
        alert('배너가 추가되었습니다')
      } catch (e) {
        console.error('배너 추가 실패:', e)
        alert('추가에 실패했습니다')
      }
    }

    onMounted(() => {
      fetchBanners()
      fetchJobs()
    })

    return {
      banners,
      jobs,
      showModal,
      showJobModal,
      isEditing,
      form,
      saving,
      jobSearchKeyword,
      filteredJobs,
      regions,
      industries,
      jobTypes,
      openAddModal,
      openEditModal,
      closeModal,
      handleImageUpload,
      saveBanner,
      toggleActive,
      deleteBanner,
      moveUp,
      moveDown,
      selectJob
    }
  }
}
</script>

<style scoped>
.admin-banners {
  padding: 32px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 24px;
  font-weight: 700;
  color: #191f28;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-primary {
  background: #3182f6;
  color: #fff;
  border: none;
}

.btn-primary:hover {
  background: #1b64da;
}

.btn-secondary {
  background: #fff;
  color: #333;
  border: 1px solid #e5e8eb;
}

.btn-secondary:hover {
  border-color: #3182f6;
  color: #3182f6;
}

/* 배너 목록 */
.banner-list {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #8b95a1;
}

.banner-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f2f4f6;
}

.banner-item:last-child {
  border-bottom: none;
}

.banner-order {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 40px;
}

.order-btn {
  width: 24px;
  height: 20px;
  background: #f2f4f6;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  color: #6b7684;
}

.order-btn:hover:not(:disabled) {
  background: #e5e8eb;
}

.order-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.banner-order span {
  font-size: 14px;
  font-weight: 600;
  color: #8b95a1;
}

.banner-preview {
  width: 120px;
  height: 68px;
  border-radius: 8px;
  overflow: hidden;
  background: #f2f4f6;
  flex-shrink: 0;
}

.banner-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-info {
  flex: 1;
  min-width: 0;
}

.banner-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.banner-info h3 {
  font-size: 15px;
  font-weight: 600;
  color: #191f28;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.banner-badges {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}

.badge.premium {
  background: #fef3c7;
  color: #92400e;
}

.badge.pick {
  background: #f3e8ff;
  color: #7c3aed;
}

.badge.urgent {
  background: #fee2e2;
  color: #b91c1c;
}

.badge.new {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge.job {
  background: #e0e7ff;
  color: #4338ca;
}

.banner-meta {
  font-size: 13px;
  color: #6b7684;
  margin-bottom: 2px;
}

.banner-meta span {
  margin-right: 8px;
}

.banner-link {
  font-size: 12px;
  color: #8b95a1;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 토글 스위치 */
.toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e5e8eb;
  transition: 0.2s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
}

.toggle input:checked + .toggle-slider {
  background-color: #3182f6;
}

.toggle input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.btn-icon {
  width: 36px;
  height: 36px;
  background: #f2f4f6;
  border: none;
  border-radius: 8px;
  color: #6b7684;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-icon:hover {
  background: #e5e8eb;
  color: #333;
}

.btn-icon.delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-large {
  max-width: 640px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f2f4f6;
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 700;
}

.close-btn {
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  color: #6b7684;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.close-btn:hover {
  background: #f2f4f6;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.15s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3182f6;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.checkbox-group {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.badge-preview {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.badge-preview.premium {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #78350f;
}

.badge-preview.pick {
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: #fff;
}

.badge-preview.urgent {
  background: #ef4444;
  color: #fff;
}

.badge-preview.new {
  background: #3182f6;
  color: #fff;
}

.active-check {
  font-weight: 500;
}

.image-upload input {
  margin-bottom: 12px;
}

.image-preview {
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  height: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f2f4f6;
}

/* 공고 선택 모달 */
.search-box {
  margin-bottom: 16px;
}

.search-box input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 14px;
}

.job-list {
  max-height: 400px;
  overflow-y: auto;
}

.job-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}

.job-item:hover {
  background: #f9fafb;
}

.job-thumb {
  width: 80px;
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
  background: #f2f4f6;
  flex-shrink: 0;
}

.job-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #8b95a1;
}

.job-info {
  flex: 1;
  min-width: 0;
}

.job-info h4 {
  font-size: 14px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.job-info p {
  font-size: 12px;
  color: #6b7684;
}

.job-info p span {
  margin-right: 8px;
}

.job-salary {
  color: #3182f6 !important;
  font-weight: 500;
}

.premium-badge {
  padding: 4px 8px;
  background: #fef3c7;
  color: #92400e;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
}

.empty-jobs {
  padding: 40px 20px;
  text-align: center;
  color: #8b95a1;
}

@media (max-width: 768px) {
  .admin-banners {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .header-actions {
    flex-direction: column;
  }

  .banner-item {
    flex-wrap: wrap;
  }

  .banner-preview {
    width: 80px;
    height: 45px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>